import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import UsageSummary from "@/components/UsageSummary/UsageSummary";
import { unwrapPaginated } from "@/config/api";
import { EMPTY_VALUE, formatDateTime } from "@/i18n/format";
import { getApiErrorMessage } from "@/lib/apiError";
import type { BillingUsage, CreditLedgerEntry, Plan } from "@/services/moderators/generated/model";
import {
  getModeratorsBillingCompaniesLedgerRetrieveQueryKey,
  getModeratorsBillingCompaniesRetrieveQueryKey,
  useModeratorsBillingCompaniesAdjustCreate,
  useModeratorsBillingCompaniesLedgerRetrieve,
  useModeratorsBillingCompaniesRetrieve,
  useModeratorsBillingCompaniesSetPlanCreate,
  useModeratorsBillingPlansList,
} from "@/services/moderators/generated/moderators-billing/moderators-billing";
import "./LeadSources.css";

const LEDGER_PARAMS = { page_size: 50 };
const DEFAULT_PLAN = "";

/**
 * WP2.4: a company's plan, credits and usage, its credit ledger, and (super
 * moderators) credit adjustments with a reason and plan assignment.
 */
const CompanyBillingCard = ({ companyId, isSuper }: { companyId: number; isSuper: boolean }) => {
  const { t } = useTranslation(["moderator", "common"]);
  const queryClient = useQueryClient();
  const summary = useModeratorsBillingCompaniesRetrieve(companyId);
  const ledger = useModeratorsBillingCompaniesLedgerRetrieve(companyId, LEDGER_PARAMS);
  const plans = unwrapPaginated<Plan>(useModeratorsBillingPlansList({ page_size: 100 }).data).filter(
    (p) => p.active !== false,
  );
  const entries = unwrapPaginated<CreditLedgerEntry>(ledger.data);
  const usage = summary.data as BillingUsage | undefined;

  const [credits, setCredits] = useState("");
  const [reason, setReason] = useState("");
  const [planCode, setPlanCode] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getModeratorsBillingCompaniesRetrieveQueryKey(companyId) });
    queryClient.invalidateQueries({
      queryKey: getModeratorsBillingCompaniesLedgerRetrieveQueryKey(companyId, LEDGER_PARAMS),
    });
  };
  const adjust = useModeratorsBillingCompaniesAdjustCreate({
    mutation: {
      onSuccess: () => {
        invalidate();
        setCredits("");
        setReason("");
        setFormError(null);
      },
      onError: (err: unknown) => setFormError(getApiErrorMessage(err, t("billing.company.adjustError"))),
    },
  });
  const setPlan = useModeratorsBillingCompaniesSetPlanCreate({
    mutation: {
      onSuccess: () => {
        invalidate();
        setPlanCode(null);
        setFormError(null);
      },
      onError: (err: unknown) => setFormError(getApiErrorMessage(err, t("billing.saveError"))),
    },
  });

  const submitAdjust = (e: React.FormEvent) => {
    e.preventDefault();
    const n = Number(credits);
    if (!Number.isInteger(n) || n === 0) {
      setFormError(t("billing.company.creditsInvalid"));
      return;
    }
    if (!reason.trim()) {
      setFormError(t("billing.company.reasonRequired"));
      return;
    }
    adjust.mutate({ id: companyId, data: { credits: n, reason: reason.trim() } });
  };

  const currentPlan = usage && !usage.plan.isDefault ? usage.plan.code : DEFAULT_PLAN;
  const selectedPlan = planCode ?? currentPlan;

  return (
    <section className="mod-page__card">
      <h2 className="mod-page__card-title">{t("billing.company.title")}</h2>
      {summary.isLoading ? <div className="mod-page__loading">{t("billing.loading")}</div> : null}
      {summary.error ? <div className="mod-page__error">{t("billing.loadError")}</div> : null}
      {usage ? <UsageSummary usage={usage} /> : null}

      {isSuper && usage ? (
        <>
          <form
            className="mod-sources__form"
            onSubmit={(e) => {
              e.preventDefault();
              setPlan.mutate({
                id: companyId,
                data: { planCode: selectedPlan === DEFAULT_PLAN ? null : selectedPlan },
              });
            }}
          >
            <select
              className="mod-page__select"
              aria-label={t("billing.company.plan")}
              value={selectedPlan}
              onChange={(e) => setPlanCode(e.target.value)}
            >
              <option value={DEFAULT_PLAN}>{t("billing.company.defaultPlan")}</option>
              {plans.map((p) => (
                <option key={p.id} value={p.code}>
                  {p.name}
                </option>
              ))}
            </select>
            <button
              type="submit"
              className="mod-page__btn mod-page__btn--primary"
              disabled={setPlan.isPending || selectedPlan === currentPlan}
            >
              {t("billing.company.assignPlan")}
            </button>
          </form>
          <form className="mod-sources__form" onSubmit={submitAdjust}>
            <input
              className="mod-page__input"
              type="number"
              step={1}
              placeholder={t("billing.company.credits")}
              aria-label={t("billing.company.credits")}
              value={credits}
              onChange={(e) => setCredits(e.target.value)}
            />
            <input
              className="mod-page__input"
              placeholder={t("billing.company.reason")}
              aria-label={t("billing.company.reason")}
              maxLength={2000}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <button type="submit" className="mod-page__btn mod-page__btn--primary" disabled={adjust.isPending}>
              {adjust.isPending ? t("common:actions.saving") : t("billing.company.adjust")}
            </button>
          </form>
        </>
      ) : null}
      {formError ? (
        <p className="mod-page__error" role="alert">
          {formError}
        </p>
      ) : null}

      <h3 className="mod-page__card-meta">{t("billing.company.ledger")}</h3>
      {!ledger.isLoading && entries.length === 0 ? (
        <div className="mod-page__empty">{t("billing.company.ledgerEmpty")}</div>
      ) : null}
      {entries.length > 0 ? (
        <div className="mod-sources__table-wrap">
          <table className="mod-sources__table">
            <thead>
              <tr>
                <th>{t("billing.company.when")}</th>
                <th>{t("billing.company.kind")}</th>
                <th className="mod-sources__count">{t("billing.company.credits")}</th>
                <th>{t("billing.company.reason")}</th>
                <th>{t("billing.company.by")}</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td>{formatDateTime(entry.createdAt)}</td>
                  <td>{t(`billing.company.kinds.${entry.kind}`)}</td>
                  <td className="mod-sources__count">{entry.delta > 0 ? `+${entry.delta}` : entry.delta}</td>
                  <td>{entry.reason || EMPTY_VALUE}</td>
                  <td>{entry.moderatorName ?? EMPTY_VALUE}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
};

export default CompanyBillingCard;
